import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Heart, Sparkles, Lock } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function InviteTeaser() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useLocale();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/companion-invites/${code}`)
      .then((r) => {
        setInvite(r.data);
        if (r.data.status !== "claimed") {
          localStorage.setItem("pending_companion_invite", r.data.code);
        }
      })
      .catch(() => setError(true));
  }, [code]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="text-center">
          <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
          <p className="text-lg font-semibold mb-2">{t("invite.expired")}</p>
          <Button onClick={() => navigate("/")} className="mt-2" data-testid="invite-expired-home-btn">
            {t("invite.explore")}
          </Button>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isDreamDate = invite.style === "dream_date";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 relative overflow-hidden" data-testid="invite-teaser-page">
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-md w-full glass-card p-8 md:p-10 text-center relative z-10 border border-rose-500/25">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Heart className="w-8 h-8 text-primary fill-primary" />
          <span className="text-2xl font-bold font-['Outfit'] tracking-tight">LuxeMeet</span>
        </div>

        {invite.companion?.photo && (
          <div className="relative inline-block mb-6">
            <img
              src={invite.companion.photo}
              alt={invite.companion.name}
              className="w-28 h-28 rounded-full object-cover mx-auto ring-4 ring-rose-500/30"
              data-testid="invite-companion-photo"
            />
            <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-rose-500 flex items-center justify-center text-lg">
              {isDreamDate ? "🌹" : "💌"}
            </span>
          </div>
        )}

        <h1 className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight mb-3" data-testid="invite-teaser-title">
          {isDreamDate
            ? t("invite.dreamTitle").replace("{name}", invite.companion?.name || "")
            : t("invite.admirerTitle")}
        </h1>
        <p className="text-muted-foreground mb-8" data-testid="invite-teaser-subtitle">
          {isDreamDate
            ? t("invite.dreamSub")
            : t("invite.admirerSub").replace("{name}", invite.companion?.name || "")}
        </p>

        {invite.status === "claimed" ? (
          <p className="text-sm text-amber-400 mb-6" data-testid="invite-claimed-note">
            {t("invite.claimedNote")}
          </p>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-6">
            <Lock className="w-4 h-4" /> {t("invite.lock")}
          </div>
        )}

        <Button
          className="w-full h-12 bg-rose-500 hover:bg-rose-400 text-white font-semibold rounded-xl mb-3"
          onClick={() => navigate("/register")}
          data-testid="invite-signup-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" /> {t("invite.reveal")}
        </Button>
        <button
          type="button"
          className="text-sm text-muted-foreground hover:text-white transition-colors"
          onClick={() => navigate("/login")}
          data-testid="invite-login-link"
        >
          {t("invite.login")}
        </button>
      </div>
    </div>
  );
}
